// src/components/ModuleDetailPage/ModuleDetailPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { modulesByCourse } from "../../assets/data/cursos";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import logo from "../../assets/logo.png";
import "./ModuleDetailPage.css";

export default function ModuleDetailPage() {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "es";

  const courseModules = modulesByCourse[courseId];
  const moduleDataBase = courseModules ? courseModules[moduleId] : null;
  const moduleDataLang =
    courseModules &&
    courseModules[`${moduleId}-${currentLang}`] &&
    courseModules[`${moduleId}-${currentLang}`];
  const moduleData = moduleDataLang || moduleDataBase;

  // ─── ESTADOS ───────────────────────────────
  const [sectionId, setSectionId] = useState(
    () => moduleData?.sections?.[0]?.id || ""
  );
  const [quizAnswers, setQuizAnswers] = useState({});
  const [score, setScore] = useState(null);

  // ─── EFECTOS ───────────────────────────────
  useEffect(() => {
    if (moduleData) {
      setSectionId(moduleData.sections?.[0]?.id || "");
      setQuizAnswers({});
      setScore(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [moduleData]);

  useEffect(() => {
    if (sectionId === "quiz") {
      setQuizAnswers({});
      setScore(null);
    }
  }, [sectionId]);

  useEffect(() => {
    if (score !== null) {
      window.scrollTo({ top: 120, behavior: "smooth" });
    }
  }, [score]);

  // ─── CALCULOS ───────────────────────────────
  const sections = moduleData?.sections || [];
  const sectionIndex = sections.findIndex((s) => s.id === sectionId);
  const moduleIds = Object.keys(modulesByCourse[courseId] || {});
  const moduleIndex = moduleIds.findIndex((m) => m === moduleId);

  const hasPrevSection = sectionIndex > 0;
  const hasNextSection = sectionIndex < sections.length - 1;
  const nextModuleId =
    moduleIndex < moduleIds.length - 1 ? moduleIds[moduleIndex + 1] : null;

  // ⚠️ Ahora el return condicional viene DESPUÉS de los hooks
  if (!moduleData) {
    return (
      <div className="module-not-found-container">
        <img src={logo} alt="ZY Logo" className="module-not-found-logo" />
        <h1 className="module-not-found-title">Módulo no encontrado</h1>
        <p className="module-not-found-message">
          Lo sentimos, el módulo que buscas no existe o ya no está disponible.
        </p>
        <div className="module-not-found-actions">
          <button className="module-back-btn" onClick={() => navigate(-1)}>
            ← Volver
          </button>
          <Link to="/" className="module-home-link">
            Ir al inicio
          </Link>
        </div>
      </div>
    );
  }

  // ─── RENDER PRINCIPAL ───────────────────────────────
  const section = moduleData.sections.find((s) => s.id === sectionId);
  const isQuiz = Array.isArray(section?.questions);
  return (
    <main className="module-detail-container">
      <section
        className="detail-banner"
        style={{ backgroundImage: `url(${moduleData.bannerUrl})` }}
      />
      <button
        className="back-btn"
        onClick={() => navigate(`/cursos/${courseId}`)}
      >
        ← Volver
      </button>

      <header className="module-header">
        <h1>{moduleData.title}</h1>
        <p className="module-info">
          <strong>Formato:</strong> {moduleData.format}
        </p>
      </header>

      <div className="module-grid">
        {/* ─── Navegación lateral ───────────────────────────── */}
        <nav className="module-nav">
          <ul>
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  className={s.id === sectionId ? "active" : ""}
                  onClick={() => setSectionId(s.id)}
                >
                  {s.navLabel}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <section
          className="mobile-banner"
          style={{ backgroundImage: `url(${moduleData.bannerUrl})` }}
        />

        <article className="module-content">
          <h2>{section.title}</h2>

          {/* ─── Sección Quiz ───────────────────────────── */}
          {isQuiz ? (
            <div className="quiz-section">
              {section.questions.map((q, idx) => {
                const userAns = quizAnswers[q.id];
                return (
                  <div key={q.id} className="quiz-question">
                    <p>
                      <strong>
                        {idx + 1}. {q.question}
                      </strong>
                    </p>
                    <ul className="quiz-options">
                      {q.options.map((opt) => {
                        const isCorrect = opt.key === q.correct;
                        const isUserChoice = userAns === opt.key;
                        const showFeedback = score !== null && isUserChoice;

                        let cls = "quiz-option";
                        if (showFeedback)
                          cls += isCorrect ? " correct" : " incorrect";

                        return (
                          <li key={opt.key} className={cls}>
                            <label>
                              <input
                                type="radio"
                                name={q.id}
                                value={opt.key}
                                checked={isUserChoice}
                                disabled={score !== null}
                                onChange={() =>
                                  setQuizAnswers((prev) => ({
                                    ...prev,
                                    [q.id]: opt.key,
                                  }))
                                }
                              />
                              {opt.key.toUpperCase()}) {opt.text}
                              {showFeedback && isCorrect && (
                                <FaCheckCircle className="feedback-icon correct-icon" />
                              )}
                              {showFeedback && !isCorrect && (
                                <FaTimesCircle className="feedback-icon incorrect-icon" />
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}

              {score === null ? (
                <button
                  className="quiz-submit"
                  disabled={
                    Object.keys(quizAnswers).length !== section.questions.length
                  }
                  onClick={() => {
                    const correct = section.questions.reduce(
                      (acc, q) =>
                        acc + (quizAnswers[q.id] === q.correct ? 1 : 0),
                      0
                    );
                    setScore(correct);
                  }}
                >
                  Enviar respuestas
                </button>
              ) : (
                <div className="quiz-complete">
                  <p className="quiz-result">
                    Obtuviste {score} de {section.questions.length} correctas (
                    {Math.round((score / section.questions.length) * 100)}%)
                  </p>
                  <button
                    className="quiz-retry"
                    onClick={() => {
                      setScore(null);
                      setQuizAnswers({});
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    ↻ Volver a intentar
                  </button>
                </div>
              )}
            </div>
          ) : (
            // ─── Secciones normales (texto, lista, links, etc.) ────────────
            section.content.map((block, i) => {
              if (typeof block === "string") return <p key={i}>{block}</p>;
              switch (block.type) {
                case "paragraph":
                  return <p key={i}>{block.text}</p>;
                case "list":
                  return (
                    <ul key={i} className="dot-list">
                      {block.items.map((it, j) => (
                        <li key={j}>{it}</li>
                      ))}
                    </ul>
                  );
                case "orderedList":
                  return (
                    <ol key={i}>
                      {block.items.map((it, j) => (
                        <li key={j}>{it}</li>
                      ))}
                    </ol>
                  );
                case "link":
                  return (
                    <p key={i}>
                      <a
                        href={block.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {block.text}
                      </a>
                    </p>
                  );
                case "table":
                  return (
                    <table key={i} className="detail-table">
                      <thead>
                        <tr>
                          {block.headers.map((h, k) => (
                            <th key={k}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.map((row, r) => (
                          <tr key={r}>
                            {row.map((cell, c) => (
                              <td key={c}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                default:
                  return null;
              }
            })
          )}

          {/* ─── Botones Anterior / Siguiente ────────────────────────── */}
          <div className="module-nav-buttons">
            <button
              className="prev-btn"
              onClick={() => setSectionId(sections[sectionIndex - 1].id)}
              disabled={!hasPrevSection}
            >
              ← Anterior
            </button>

            <button
              className="next-btn"
              onClick={() => {
                if (hasNextSection) {
                  setSectionId(sections[sectionIndex + 1].id);
                } else if (nextModuleId) {
                  navigate(`/cursos/${courseId}/${nextModuleId}`);
                }
              }}
              disabled={!hasNextSection && !nextModuleId}
            >
              Siguiente →
            </button>
          </div>
        </article>
      </div>
    </main>
  );
}
